import { useCallback, useMemo, useState } from 'react'
import { quizQuestions } from '../data/quiz'
import { codes } from '../data/codes'
import type { CodeKey, LeadDetails } from '../types/quiz'

export type QuizStage = 'intro' | 'questions' | 'capture' | 'result'

const scorableQuestions = quizQuestions.filter((q) => q.kind !== 'open')

export function useQuiz() {
  const [stage, setStage] = useState<QuizStage>('intro')
  const [index, setIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const [openText, setOpenText] = useState('')
  const [lead, setLead] = useState<LeadDetails | null>(null)

  const question = quizQuestions[index]
  const total = quizQuestions.length
  const isLast = index === total - 1

  const currentAnswer = answers[question?.id ?? -1]

  const canContinue =
    question?.kind === 'open' ? true : Boolean(currentAnswer)

  const select = useCallback(
    (questionId: number, label: string) => {
      setAnswers((prev) => ({ ...prev, [questionId]: label }))
    },
    []
  )

  const next = useCallback(() => {
    if (isLast) {
      setStage('capture')
      return
    }
    setIndex((i) => Math.min(i + 1, total - 1))
  }, [isLast, total])

  const back = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0))
  }, [])

  const start = useCallback(() => {
    setStage('questions')
    setIndex(0)
  }, [])

  const scores = useMemo(() => {
    const tally: Record<CodeKey, number> = {
      presencia: 0,
      confianza: 0,
      alineacion: 0,
    }

    scorableQuestions.forEach((q) => {
      const answerLabel = answers[q.id]
      if (!answerLabel) return
      const option = q.options?.find((o) => o.label === answerLabel)
      if (!option) return
      if (option.code === 'all') {
        tally.presencia += 1
        tally.confianza += 1
        tally.alineacion += 1
      } else {
        tally[option.code] += 1
      }
    })

    return tally
  }, [answers])

  const result = useMemo(() => {
    // Ties resolve in the canonical order of the three codes.
    let winner = codes[0]
    let best = -1
    codes.forEach((code) => {
      if (scores[code.key] > best) {
        best = scores[code.key]
        winner = code
      }
    })
    return winner
  }, [scores])

  const submitLead = useCallback((details: LeadDetails) => {
    setLead(details)
    setStage('result')
  }, [])

  const restart = useCallback(() => {
    setAnswers({})
    setOpenText('')
    setLead(null)
    setIndex(0)
    setStage('intro')
  }, [])

  return {
    stage,
    index,
    total,
    question,
    currentAnswer,
    canContinue,
    isLast,
    openText,
    setOpenText,
    select,
    next,
    back,
    start,
    scores,
    result,
    lead,
    submitLead,
    restart,
  }
}
